import {
  BottomSheet,
  BottomSheetLoadingContent,
  Illustration,
  Text,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack, ThemeProvider } from "@mui/material";
import { type GeoLocation } from "@src/appV2/Location/types";
import { TopRightCloseButton } from "@src/appV2/redesign/components/TopRightCloseButton";
import { differenceInMinutes } from "date-fns";
import { useEffect } from "react";

import { type ChartingNoncompliance } from "./api/schema";
import { useMarkChartingForReview } from "./api/useMarkChartingForReview";
import { ChartingReviewFooter } from "./components/ChartingReviewFooter";
import { CHARTING_ERROR_CODES, CHARTING_REVIEW_DURATION_IN_HOURS } from "./constants";
import { chartingDeadlineHasPassed } from "./utils/helpers";

interface ChartingNoncomplianceReviewBottomSheetProps {
  chartingNoncomplianceReviewModalState: UseModalState;
  workerId: string;
  workplaceId: string;
  workplaceName: string;
  workplaceCoordinates: GeoLocation | undefined;
  chartingNoncompliance: ChartingNoncompliance;
}

export function ChartingNoncomplianceReviewBottomSheet({
  chartingNoncomplianceReviewModalState,
  workerId,
  workplaceId,
  workplaceName,
  workplaceCoordinates,
  chartingNoncompliance,
}: ChartingNoncomplianceReviewBottomSheetProps) {
  const {
    id: chartingNoncomplianceId,
    attributes: { deadlineToReview },
  } = chartingNoncompliance;
  const {
    mutate: verifyAndPatch,
    isLoading,
    isSuccess,
    isError,
    error,
    reset,
  } = useMarkChartingForReview({
    workerId,
    workplaceId,
    chartingNoncomplianceId,
    workplaceCoordinates,
  });

  const isInReview = isDefined(deadlineToReview) && !chartingDeadlineHasPassed(deadlineToReview);
  const hoursUntilReviewDeadline = isDefined(deadlineToReview)
    ? Math.ceil(differenceInMinutes(deadlineToReview, new Date()) / 60)
    : CHARTING_REVIEW_DURATION_IN_HOURS;

  const daysUntilReviewDeadline = Math.ceil(hoursUntilReviewDeadline / 24);
  const daysUntilReviewDeadlineDisplay =
    daysUntilReviewDeadline === 1 ? "a day" : `${daysUntilReviewDeadline} days`;

  useEffect(() => {
    if (
      chartingNoncomplianceReviewModalState.modalIsOpen &&
      !isLoading &&
      !isSuccess &&
      !isError &&
      !isInReview
    ) {
      verifyAndPatch();
    }
  }, [
    chartingNoncomplianceReviewModalState.modalIsOpen,
    verifyAndPatch,
    isLoading,
    isSuccess,
    isError,
    isInReview,
  ]);

  const workplaceText = `${workplaceName} will review your charting within ${daysUntilReviewDeadlineDisplay}. In the meantime, you can book and work shifts as usual`;

  return (
    <ThemeProvider theme={getTheme()}>
      <BottomSheet
        modalState={chartingNoncomplianceReviewModalState}
        canDismiss={false}
        footer={
          !isLoading && (
            <ChartingReviewFooter
              chartingNoncomplianceReviewModalState={chartingNoncomplianceReviewModalState}
              workerId={workerId}
              workplaceId={workplaceId}
              isSuccess={isSuccess || isInReview}
              checkGeofenceStatus={() => {
                reset();
                verifyAndPatch();
              }}
            />
          )
        }
      >
        <Box
          sx={{
            paddingX: 4,
            paddingTop: 8,
            paddingBottom: 4,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {isLoading && (
            <BottomSheetLoadingContent
              title="Confirming your location"
              description="This can take up to 10 seconds"
            />
          )}

          {(isSuccess || isInReview) && (
            <Stack spacing={6} sx={{ paddingX: 8, paddingY: 4, alignItems: "center" }}>
              <Illustration type="clock" />
              <Text bold sx={{ textAlign: "center" }} variant="h3">
                Your charting is under review
              </Text>
              <Text sx={{ textAlign: "center" }} variant="body1">
                {workplaceText}
              </Text>
            </Stack>
          )}

          {isError && (
            <>
              <TopRightCloseButton onClick={chartingNoncomplianceReviewModalState.closeModal} />
              <Stack spacing={6} sx={{ paddingX: 8, paddingY: 4, alignItems: "center" }}>
                <Illustration type="location-error" />
                <Text bold sx={{ textAlign: "center" }} variant="h3">
                  {error?.message === CHARTING_ERROR_CODES.OUTSIDE_GEOFENCE
                    ? "Too far away"
                    : "Something went wrong"}
                </Text>
                <Text sx={{ textAlign: "center" }} variant="body1">
                  {error?.message === CHARTING_ERROR_CODES.OUTSIDE_GEOFENCE
                    ? `It looks like you are not inside ${workplaceName}. Please try again when you are inside the workplace`
                    : "Please try again or contact support if the problem persists"}
                </Text>
              </Stack>
            </>
          )}
        </Box>
      </BottomSheet>
    </ThemeProvider>
  );
}
